import React, { ReactNode } from 'react';
import {
  Box,
  Flex,
  Heading,
  Spacer,
  Button,
  HStack,
  Container,
  Text,
  VStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Avatar,
  Badge,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  useDisclosure,
  useBreakpointValue,
  Portal,
  useToast,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaBell, 
  FaPlus, 
  FaUser, 
  FaCog, 
  FaSignOutAlt, 
  FaBars, 
  FaHeart,
  FaEnvelope,
  FaBox,
  FaQuestionCircle,
  FaDatabase,
} from 'react-icons/fa';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isMobile = useBreakpointValue({ base: true, md: false });
  
  // Use auth context
  const { user: currentUser, isAuthenticated, logout } = useAuth();

  const handleSignOut = () => {
    logout();
    
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out',
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    
    navigate('/');
  };

  const handleCreatePost = () => {
    if (!isAuthenticated) {
      navigate('/login');
    } else {
      navigate('/create-post');
    }
  };

  const NavItem = ({ 
    path, 
    children, 
    isActive, 
    onClick 
  }: { 
    path?: string; 
    children: React.ReactNode; 
    isActive?: boolean;
    onClick?: () => void;
  }) => (
    <Button
      onClick={onClick || (() => path && navigate(path))}
      variant={isActive ? 'solid' : 'ghost'}
      size="md"
      color={isActive ? 'black' : 'brand.green.medium'}
      bg={isActive ? 'brand.primary' : 'transparent'}
      _hover={{
        color: isActive ? 'black' : 'brand.green.dark',
        bg: isActive ? 'brand.primaryHover' : 'brand.green.50',
      }}
    >
      {children}
    </Button>
  );

  const MobileNavigation = () => (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Logo size="sm" />
        </DrawerHeader>
        <DrawerBody>
          <VStack spacing={4} align="stretch">
            <NavItem 
              path="/" 
              isActive={location.pathname === '/'}
            >
              Home
            </NavItem>
            <NavItem 
              path="/browse" 
              isActive={location.pathname === '/browse'}
            >
              Browse
            </NavItem>
            
            {isAuthenticated ? (
              <>
                <Button
                  leftIcon={<FaPlus />}
                  onClick={handleCreatePost}
                  bg="brand.primary"
                  color="black"
                  _hover={{ bg: 'brand.primaryHover' }}
                  size="md"
                >
                  Sell Item
                </Button>
                
                <NavItem path="/profile">
                  My Profile
                </NavItem>
                <NavItem path="/my-posts">
                  My Listings
                </NavItem>
                <NavItem path="/favorites">
                  Favorites
                </NavItem>
                <NavItem path="/messages">
                  Messages
                </NavItem>
                <NavItem path="/settings">
                  Settings
                </NavItem>
                
                <Button
                  leftIcon={<FaSignOutAlt />}
                  onClick={handleSignOut}
                  variant="ghost"
                  colorScheme="red"
                  justifyContent="flex-start"
                >
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <NavItem 
                  path="/login" 
                  isActive={location.pathname === '/login'}
                >
                  Login
                </NavItem>
                <NavItem path="/signup">
                  Join ReDeuce
                </NavItem>
              </>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  const DesktopNavigation = () => (
    <HStack spacing={4}>
      <NavItem 
        path="/" 
        isActive={location.pathname === '/'}
      >
        Home
      </NavItem>
      <NavItem 
        path="/browse" 
        isActive={location.pathname === '/browse'}
      >
        Browse
      </NavItem>
      
      {isAuthenticated ? (
        <>
          {/* Sell Button */}
          <Button
            leftIcon={<FaPlus />}
            onClick={handleCreatePost}
            bg="brand.primary"
            color="black"
            _hover={{ 
              bg: 'brand.primaryHover',
              transform: 'translateY(-2px)',
              shadow: 'lg'
            }}
            size="md"
            fontWeight="bold"
          >
            Sell Item
          </Button>
          
          {/* Notifications */}
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Notifications"
              icon={<FaBell />}
              variant="ghost"
              size="md"
              position="relative"
              color="brand.green.medium"
              _hover={{ color: 'brand.green.dark', bg: 'brand.green.50' }}
            >
              {/* Remove notification count since it's not in User type */}
            </MenuButton>
          </Menu>
          
          {/* User Menu */}
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              size="md"
              leftIcon={
                <Avatar 
                  size="sm" 
                  src={currentUser?.profilePicture} 
                  name={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : undefined}
                />
              }
              rightIcon={currentUser?.isVerified ? <Badge colorScheme="green" fontSize="xs">✓</Badge> : undefined}
              _hover={{ bg: 'brand.green.50' }}
            >
              <Text display={{ base: 'none', lg: 'block' }}>
                {currentUser?.firstName}
              </Text>
            </MenuButton>
            <Portal>
              <MenuList>
                <Box px={3} py={2}>
                  <Text fontWeight="semibold" fontSize="sm">
                    {currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : ''}
                  </Text>
                  <Text fontSize="xs" color="brand.gray.500">
                    {currentUser?.email}
                  </Text>
                  {currentUser?.isVerified && (
                    <Badge colorScheme="green" size="sm" mt={1}>
                      Verified Seller
                    </Badge>
                  )}
                </Box>
                <MenuDivider />
                <MenuItem icon={<FaUser />} onClick={() => navigate('/profile')}>
                  My Profile
                </MenuItem>
                <MenuItem icon={<FaBox />} onClick={() => navigate('/my-posts')}>
                  My Listings
                </MenuItem>
                <MenuItem icon={<FaHeart />} onClick={() => navigate('/favorites')}>
                  Favorites
                </MenuItem>
                <MenuItem icon={<FaEnvelope />} onClick={() => navigate('/messages')}>
                  Messages
                </MenuItem>
                <MenuDivider />
                <MenuItem icon={<FaCog />} onClick={() => navigate('/settings')}>
                  Settings
                </MenuItem>
                <MenuItem icon={<FaQuestionCircle />} onClick={() => navigate('/help')}>
                  Help & Support
                </MenuItem>
                <MenuDivider />
                <MenuItem icon={<FaDatabase />} onClick={() => navigate('/admin-seeding-panel')}>
                  Admin Seeding Panel
                </MenuItem>
                <MenuDivider />
                <MenuItem 
                  icon={<FaSignOutAlt />} 
                  onClick={handleSignOut}
                  color="red.500"
                >
                  Sign Out
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </>
      ) : (
        <>
          <NavItem 
            path="/login" 
            isActive={location.pathname === '/login'}
          >
            Login
          </NavItem>
          <Button
            onClick={() => navigate('/signup')}
            bg="brand.primary"
            color="black"
            _hover={{ 
              bg: 'brand.primaryHover',
              transform: 'translateY(-2px)',
              shadow: 'lg'
            }}
            size="md"
            fontWeight="bold"
          >
            Join ReDeuce
          </Button>
        </>
      )}
    </HStack>
  );
  
  return (
    <Flex direction="column" minH="100vh" bg="white">
      {/* Header */}
      <Box 
        bg="white" 
        shadow="lg" 
        position="sticky" 
        top="0" 
        zIndex="sticky"
        borderBottom="3px solid"
        borderColor="brand.primary"
      >
        <Container maxW="7xl">
          <Flex py={4} align="center">
            {/* Logo Section */}
            <Flex 
              align="center" 
              cursor="pointer"
              onClick={() => navigate('/')}
              _hover={{ transform: 'scale(1.05)' }}
              transition="all 0.2s"
            >
              <Logo size="md" />
            </Flex>
            
            <Spacer />
            
            {/* Desktop Navigation */}
            {!isMobile && <DesktopNavigation />}
            
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                aria-label="Open menu"
                icon={<FaBars />}
                variant="ghost"
                size="lg"
                color="brand.green.medium"
                _hover={{ color: 'brand.green.dark', bg: 'brand.green.50' }}
                onClick={onOpen}
              />
            )}
          </Flex>
        </Container>
      </Box>

      {/* Mobile Navigation Drawer */}
      <MobileNavigation />

      {/* Main Content */}
      <Box as="main" flex="1">
        {children}
      </Box>

      {/* Footer */}
      <Box 
        bg="brand.green.dark" 
        color="white" 
        py={12} 
        mt={16}
        position="relative"
        overflow="hidden"
      >
        {/* Background pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.1"
          bgGradient="linear(45deg, brand.primary 25%, transparent 25%, transparent 75%, brand.primary 75%)"
          bgSize="60px 60px"
        />
        
        <Container maxW="7xl" position="relative">
          <Flex direction={{ base: 'column', md: 'row' }} align="center">
            <Flex align="center" mb={{ base: 6, md: 0 }}>
              <Logo variant="white" size="sm" mr={4} />
              <VStack spacing={1} align="start">
                <Heading size="md" color="white">
                  ReDeuce
                </Heading>
                <Text fontSize="sm" color="brand.primary" fontWeight="medium">
                  Sustainable Tennis Marketplace
                </Text>
              </VStack>
            </Flex>
            
            <Spacer />
            
            <VStack spacing={2} align={{ base: 'center', md: 'end' }}>
              <HStack spacing={6}>
                <Text fontSize="sm" opacity={0.8} cursor="pointer" _hover={{ opacity: 1 }}>
                  About
                </Text>
                <Text fontSize="sm" opacity={0.8} cursor="pointer" _hover={{ opacity: 1 }}>
                  Contact
                </Text>
                <Text fontSize="sm" opacity={0.8} cursor="pointer" _hover={{ opacity: 1 }}>
                  Privacy
                </Text>
                <Text fontSize="sm" opacity={0.8} cursor="pointer" _hover={{ opacity: 1 }}>
                  Terms
                </Text>
              </HStack>
              <Text fontSize="sm" opacity={0.7}>
                © 2024 ReDeuce. All rights reserved.
              </Text>
            </VStack>
          </Flex>
        </Container>
      </Box>
    </Flex>
  );
};

export default Layout; 